/*
  Based on Vuetifies ripple directive that is covered under the MIT license
  https://github.com/vuetifyjs/vuetify
*/
import './wave.scss';
import { VNode, VNodeDirective } from 'vue';
import { EKeyCode } from './types/enums/key-code';
import { WaveOptions } from './types/wave-options.type';
import { WaveEvent } from './types/wave-event.type';
import { HTMLElementWave } from './types/wave-element.type';

const DELAY_wave = 80;

function transform(el: HTMLElement, value: string) {
  el.style.transform = value;
  el.style.webkitTransform = value;
}

function opacity(el: HTMLElement, value: number) {
  el.style.opacity = value.toString();
}

function isTouchEvent(e: WaveEvent): e is TouchEvent {
  return e.constructor.name === 'TouchEvent';
}

function isKeyboardEvent(e: WaveEvent): e is KeyboardEvent {
  return e.constructor.name === 'KeyboardEvent';
}

const calculate = (e: WaveEvent, el, value: WaveOptions = {}) => {
  let localX = 0;
  let localY = 0;

  if (!isKeyboardEvent(e)) {
    const offset = el.getBoundingClientRect();
    const target = isTouchEvent(e) ? e.touches[e.touches.length - 1] : e;

    localX = target.clientX - offset.left;
    localY = target.clientY - offset.top;
  }

  let radius = 0;
  let scale = 0.3;
  if (el._wave && el._wave.circle) {
    scale = 0.15;
    radius = el.clientWidth / 2;
    radius = value.center
      ? radius
      : radius + Math.sqrt((localX - radius) ** 2 + (localY - radius) ** 2) / 4;
  } else {
    radius = Math.sqrt(el.clientWidth ** 2 + el.clientHeight ** 2) / 2;
  }

  const centerX = `${(el.clientWidth - radius * 2) / 2}px`;
  const centerY = `${(el.clientHeight - radius * 2) / 2}px`;

  const x = value.center ? centerX : `${localX - radius}px`;
  const y = value.center ? centerY : `${localY - radius}px`;

  return { radius, scale, x, y, centerX, centerY };
};

const waves = {
  show(e: WaveEvent, el: HTMLElementWave, value: WaveOptions = {}) {
    if (!el._wave || !el._wave.enabled) {
      return;
    }

    const container = document.createElement('span');
    const animation = document.createElement('span');

    container.appendChild(animation);
    container.className = 'v-wave__container';

    if (value.class) {
      container.className += ` ${value.class}`;
    }

    const { radius, scale, x, y, centerX, centerY } = calculate(e, el, value);

    const size = `${radius * 2}px`;
    // eslint-disable-next-line sonarjs/no-duplicate-string
    animation.className = 'v-wave__animation';
    animation.style.width = size;
    animation.style.height = size;

    el.appendChild(container);

    const computed = window.getComputedStyle(el);
    if (computed && computed.position === 'static') {
      el.style.position = 'relative';
      el.dataset.previousPosition = 'static';
    }

    animation.classList.add('v-wave__animation--enter');
    animation.classList.add('v-wave__animation--visible');
    transform(animation, `translate(${x}, ${y}) scale3d(${scale},${scale},${scale})`);
    opacity(animation, 0);
    animation.dataset.activated = String(performance.now());

    setTimeout(() => {
      animation.classList.remove('v-wave__animation--enter');
      animation.classList.add('v-wave__animation--in');
      transform(animation, `translate(${centerX}, ${centerY}) scale3d(1,1,1)`);
      opacity(animation, 0.25);
    }, 0);
  },

  hide(el: HTMLElementWave | null) {
    if (!el || !el._wave || !el._wave.enabled) return;

    const waves = el.getElementsByClassName('v-wave__animation');

    if (waves.length === 0) return;
    const animation = waves[waves.length - 1] as HTMLElement;

    if (animation.dataset.isHiding) return;
    else animation.dataset.isHiding = 'true';

    const diff = performance.now() - Number(animation.dataset.activated);
    const delay = Math.max(250 - diff, 0);

    setTimeout(() => {
      animation.classList.remove('v-wave__animation--in');
      animation.classList.add('v-wave__animation--out');
      opacity(animation, 0);

      setTimeout(() => {
        const waves = el.getElementsByClassName('v-wave__animation');
        if (waves.length === 1 && el.dataset.previousPosition) {
          el.style.position = el.dataset.previousPosition;
          delete el.dataset.previousPosition;
        }

        animation.parentNode && el.removeChild(animation.parentNode);
      }, 300);
    }, delay);
  },
};

function isWaveEnabled(value: any): value is true {
  return typeof value === 'undefined' || !!value;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function waveShow(e: WaveEvent) {
  const value: WaveOptions = {};
  const element = e.currentTarget as HTMLElementWave;
  if (!element || !element._wave || element._wave.touched) return;
  if (isTouchEvent(e)) {
    element._wave.touched = true;
    element._wave.isTouch = true;
  } else {
    if (element._wave.isTouch) return;
  }
  value.center = element._wave.centered || isKeyboardEvent(e);
  if (element._wave.class) {
    value.class = element._wave.class;
  }

  if (isTouchEvent(e)) {
    // already queued that shows or hides the wave
    if (element._wave.showTimerCommit) return;

    element._wave.showTimerCommit = () => {
      waves.show(e, element, value);
    };
    element._wave.showTimer = window.setTimeout(() => {
      if (element && element._wave && element._wave.showTimerCommit) {
        element._wave.showTimerCommit();
        element._wave.showTimerCommit = null;
      }
    }, DELAY_wave);
  } else {
    waves.show(e, element, value);
  }
}

function waveHide(e: Event) {
  const element = e.currentTarget as HTMLElementWave | null;
  if (!element || !element._wave) return;

  window.clearTimeout(element._wave.showTimer);

  // The touch interaction occurs before the show timer is triggered.
  // We still want to show wave effect.
  if (e.type === 'touchend' && element._wave.showTimerCommit) {
    element._wave.showTimerCommit();
    element._wave.showTimerCommit = null;

    // re-queue wave hiding
    element._wave.showTimer = setTimeout(() => {
      waveHide(e);
    });
    return;
  }

  window.setTimeout(() => {
    if (element._wave) {
      element._wave.touched = false;
    }
  });
  waves.hide(element);
}

function waveCancelShow(e: MouseEvent | TouchEvent) {
  const element = e.currentTarget as HTMLElementWave | undefined;

  if (!element || !element._wave) return;

  if (element._wave.showTimerCommit) {
    element._wave.showTimerCommit = null;
  }

  window.clearTimeout(element._wave.showTimer);
}

let keyboardWave = false;

function keyboardWaveShow(e: KeyboardEvent) {
  if (!keyboardWave && (e.keyCode === EKeyCode.Enter || e.keyCode === EKeyCode.Space)) {
    keyboardWave = true;
    waveShow(e);
  }
}

function keyboardWaveHide(e: KeyboardEvent) {
  keyboardWave = false;
  waveHide(e);
}

function updateWave(el: HTMLElementWave, binding: VNodeDirective, wasEnabled: boolean) {
  const enabled = isWaveEnabled(binding.value);
  if (!enabled) {
    waves.hide(el);
  }
  el._wave = el._wave || {};
  el._wave.enabled = enabled;
  const value = binding.value || {};
  if (value.center) {
    el._wave.centered = true;
  }
  if (value.class) {
    el._wave.class = binding.value.class;
  }
  if (value.circle) {
    el._wave.circle = value.circle;
  }
  if (enabled && !wasEnabled) {
    el.addEventListener('touchstart', waveShow, { passive: true });
    el.addEventListener('touchend', waveHide, { passive: true });
    el.addEventListener('touchmove', waveCancelShow, { passive: true });
    el.addEventListener('touchcancel', waveHide);

    el.addEventListener('mousedown', waveShow);
    el.addEventListener('mouseup', waveHide);
    el.addEventListener('mouseleave', waveHide);

    el.addEventListener('keydown', keyboardWaveShow);
    el.addEventListener('keyup', keyboardWaveHide);

    el.addEventListener('dragstart', waveHide, { passive: true });
  } else if (!enabled && wasEnabled) {
    removeListeners(el);
  }
}

function removeListeners(el: HTMLElement) {
  el.removeEventListener('mousedown', waveShow);
  el.removeEventListener('touchstart', waveShow);
  el.removeEventListener('touchend', waveHide);
  el.removeEventListener('touchmove', waveCancelShow);
  el.removeEventListener('touchcancel', waveHide);
  el.removeEventListener('mouseup', waveHide);
  el.removeEventListener('mouseleave', waveHide);
  el.removeEventListener('keydown', keyboardWaveShow);
  el.removeEventListener('keyup', keyboardWaveHide);
  el.removeEventListener('dragstart', waveHide);
}

function directive(el: HTMLElement, binding: VNodeDirective, node: VNode) {
  updateWave(el, binding, false);

  if (process.env.NODE_ENV === 'development') {
    node.context &&
      node.context.$nextTick(() => {
        const computed = window.getComputedStyle(el);
        if (computed && computed.display === 'inline') {
          console.warn(`v-wave can only be used on block-level elements, but current el is ${el}`);
        }
      });
  }
}

function unbind(el) {
  delete el._wave;
  removeListeners(el);
}

function update(el: HTMLElement, binding: VNodeDirective) {
  if (binding.value === binding.oldValue) {
    return;
  }

  const wasEnabled = isWaveEnabled(binding.oldValue);
  updateWave(el, binding, wasEnabled);
}

export const Wave = {
  name: 'wave',
  bind: directive,
  unbind,
  update,
};

export default Wave;
